import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, StatusTip } from 'tea-component';
import { useIntl } from 'umi';

import MultipleCascaderPanel from './Panel';
import type {
  IMultipleCascaderProps,
  TCascaderChangeContext,
  TStringPath,
} from './types';
import {
  getDefaultDisplayText,
  getSelectedOptions,
} from './utils';

import './index.scss';

/**
 * MultipleCascader：Datalab 多选级联选择器（Dropdown + 自研 Panel）。
 *
 * 模块职责：
 * - 负责受控/非受控选中值管理（`value` vs `defaultValue`）与触发区回显。
 * - 负责 loading/error/空数据等“状态兜底”渲染，避免面板在数据未就绪时展示残缺 UI。
 * - 负责面板搜索关键字的生命周期（关闭时默认清空，可通过开关保留）。
 *
 * 强依赖：
 * - 依赖 `tea-component` 的 `Dropdown` 行为与 overlay 容器结构（样式在 `index.scss` 中按类名约定覆盖）。
 */
const MultipleCascader: React.FC<IMultipleCascaderProps> = (props) => {
  const {
    data,
    value,
    defaultValue,
    onChange,
    columnTitles,
    valueRender,
    placeholder,
    clearable,
    disabled,
    className,
    overlayClassName,
    loading,
    error,
    searchable,
    searchPlaceholder,
    onSearch,
    clearSearchOnClose,
    ...restProps
  } = props;

  const { formatMessage } = useIntl();

  const [innerValue, setInnerValue] = useState<TStringPath[]>(
    () => value || defaultValue || [],
  );

  useEffect(() => {
    if (Array.isArray(value)) {
      // 受控模式下以 props.value 为准：避免外部状态更新后触发区回显滞后。
      setInnerValue(value);
    }
  }, [value]);

  // 统一的“有效选中值”读取口径：受控优先，其次用内部状态。
  const selectedValue = value || innerValue || [];

  const effectiveSearchable = searchable !== false;
  const shouldClearSearchOnClose = clearSearchOnClose !== false;
  const [searchValue, setSearchValue] = useState<string>('');

  const handleInternalChange = useCallback(
    (nextValue: TStringPath[], context: TCascaderChangeContext) => {
      const isControlled = Array.isArray(value);
      if (!isControlled) {
        setInnerValue(nextValue);
      }
      onChange?.(nextValue, context);
    },
    [onChange, value],
  );

  const selectedChains = useMemo(
    () => getSelectedOptions(data, selectedValue),
    [data, selectedValue],
  );

  const displayNode = useMemo(() => {
    if (!selectedValue.length) {
      return (
        <span className="dbl-multiple-cascader-trigger-placeholder">
          {placeholder || ''}
        </span>
      );
    }

    if (typeof valueRender === 'function') {
      return valueRender(selectedChains);
    }

    const text = getDefaultDisplayText(selectedChains);

    return (
      <span title={text}>
        {text}
      </span>
    );
  }, [placeholder, selectedChains, selectedValue.length]);

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      if (!selectedValue.length || disabled) {
        return;
      }
      handleInternalChange([], { type: 'clear', value: [] });
    },
    [disabled, handleInternalChange, selectedValue.length],
  );

  const triggerNode = (
    <div
      className={classNames('dbl-multiple-cascader-trigger', {
        'is-disabled': disabled,
      })}
    >
      <div className="dbl-multiple-cascader-trigger-value">{displayNode}</div>
      {clearable && selectedValue.length > 0 && !disabled ? (
        <span
          className="dbl-multiple-cascader-trigger-clear"
          onClick={handleClear}
        >
          ×
        </span>
      ) : null}
    </div>
  );

  const overlayContent = useMemo(() => {
    if (loading) {
      return (
        <div className="dbl-multiple-cascader-status">
          <StatusTip
            status="loading"
            loadingText={formatMessage({ id: 'dbl_common_loading' })}
          />
        </div>
      );
    }

    if (error) {
      const errorText =
        typeof error === 'string' && error.trim()
          ? error
          : formatMessage({ id: 'dbl_common_load_fail' });

      return (
        <div className="dbl-multiple-cascader-status">
          <StatusTip status="error" errorText={errorText} />
        </div>
      );
    }

    const isEmptyData = !Array.isArray(data) || data.length === 0;

    if (isEmptyData) {
      return (
        <div className="dbl-multiple-cascader-status">
          {formatMessage({ id: 'dbl_common_no_data' })}
        </div>
      );
    }

    return (
      <MultipleCascaderPanel
        data={data}
        value={selectedValue}
        columnTitles={columnTitles}
        onChange={handleInternalChange}
        disabled={disabled}
        searchable={effectiveSearchable}
        searchPlaceholder={searchPlaceholder}
        onSearch={onSearch}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        {...restProps}
      />
    );
  }, [
    columnTitles,
    data,
    error,
    formatMessage,
    handleInternalChange,
    loading,
    disabled,
    effectiveSearchable,
    onSearch,
    searchPlaceholder,
    searchValue,
    selectedValue,
    restProps,
  ]);

  return (
    <div className={classNames('dbl-multiple-cascader', className)}>
      <Dropdown
        {...(restProps)}
        updateOnChildrenChange
        overlayClassName={classNames(
          'dbl-multiple-cascader-overlay',
          overlayClassName,
        )}
        size="m"
        appearance="button"
        trigger="click"
        clickClose={false}
        disabled={disabled}
        button={triggerNode}
        onClose={() => {
          if (!effectiveSearchable || !shouldClearSearchOnClose) {
            return;
          }
          // 需求约束：关闭弹窗默认清空关键字，避免“再次打开仍处于过滤态”造成误解；
          // 业务若需要保留（例如对比多个选项）可显式传 clearSearchOnClose=false。
          setSearchValue('');
        }}
      >
        {overlayContent}
      </Dropdown>
    </div>
  );
};

export default MultipleCascader;

